import { FC, useEffect, useState } from 'react';
import { DomainSelection } from './steps/domain-selection.tsx';
import { ActivityInput, Domain } from '../../graphql/generated/graphql.ts';
import { ActivitySuggestions } from './steps/activity-suggestions.tsx';
import { TypeSelection } from './steps/type-selection.tsx';
import { TimeSelection } from './steps/time-selection.tsx';
import { SubGoalSelection } from './steps/sub-goal-selection.tsx';
import { Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useCreateActivityMutation } from '../../graphql/generated/operations/activity.ts';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../constants/paths.ts';
import { Dialogs } from '../../constants/dialogs.ts';
import { useDialog } from '../../contexts/dialog.context.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { trackEvent } from '../../utils/analytics.ts';

export const ActivityPlanningPage: FC = () => {
  const { openDialog } = useDialog();
  const [activeStep, setActiveStep] = useState(0);
  const [input, setInput] = useState<Partial<ActivityInput>>({});
  const [domain, setDomain] = useState<Partial<Domain>>({});
  const [createActivity, { data }] = useCreateActivityMutation();
  const navigate = useNavigate();

  const handleActivityPlanned = () => {
    openDialog(Dialogs.PLAN_ACTIVITIES);
  };

  useEffect(() => {
    if (activeStep === 5) {
      trackEvent('activity_planning_submit');
      input.offset = Math.floor(new Date().getTimezoneOffset() / 60); // Timezone offset in hours
      createActivity({
        variables: {
          input: input as ActivityInput
        }
      });
    }
  }, [activeStep]);

  useEffect(() => {
    if (data?.createActivity) {
      trackEvent('activity_planning_success');
      handleActivityPlanned();
      navigate(Paths.HOME);
    }
  }, [data]);

  if (activeStep === 0) {
    return (
      <>
        <Button
          onClick={() => {
            navigate(-1);
            trackEvent('activity_planning_back', { step: 0 });
          }}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          Back
        </Button>
        <DomainSelection
          onNext={(newInput, domain) => {
            setInput(newInput);
            setDomain(domain);
            setActiveStep(1);
            trackEvent('activity_planning_step', { step: 1 });
          }}
        />
      </>
    );
  }
  if (activeStep === 1) {
    return (
      <>
        <Button
          onClick={() => {
            setActiveStep(0);
            trackEvent('activity_planning_back', { step: 1 });
          }}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          Back
        </Button>
        <ActivitySuggestions
          input={input}
          domain={domain}
          onNext={(newInput) => {
            setInput(newInput);
            setActiveStep(2);
            trackEvent('activity_planning_step', { step: 2 });
          }}
        />
      </>
    );
  }
  if (activeStep === 2) {
    return (
      <>
        <Button
          onClick={() => {
            setActiveStep(1);
            trackEvent('activity_planning_back', { step: 2 });
          }}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          Back
        </Button>
        <TypeSelection
          domain={domain}
          input={input}
          onNext={(newInput) => {
            setInput(newInput);
            setActiveStep(3);
            trackEvent('activity_planning_step', { step: 3 });
          }}
        />
      </>
    );
  }
  if (activeStep === 3) {
    return (
      <>
        <Button
          onClick={() => {
            setActiveStep(2);
            trackEvent('activity_planning_back', { step: 3 });
          }}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          Back
        </Button>
        <SubGoalSelection
          domain={domain}
          input={input}
          onNext={(newInput) => {
            setInput(newInput);
            setActiveStep(4);
            trackEvent('activity_planning_step', { step: 4 });
          }}
        />
      </>
    );
  }
  if (activeStep === 4) {
    return (
      <>
        <Button
          onClick={() => {
            setActiveStep(3);
            trackEvent('activity_planning_back', { step: 4 });
          }}
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
        >
          Back
        </Button>
        <TimeSelection
          domain={domain}
          input={input}
          onNext={(newInput) => {
            setInput(newInput);
            setActiveStep(5);
            trackEvent('activity_planning_step', { step: 5 });
          }}
        />
      </>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={2} direction="row" alignItems="center">
          <CircularProgress />
          <Typography variant="h4">Creating Activity...</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
